import React, { useState, useEffect } from 'react';
import axios from 'axios';
import '../styles/AttributionClasses.css';
import DashboardHeader from '../components/DashboardHeader';
import AdminNavigationBar from '../components/AdminNavigationBar';

const API_BASE_URL = 'http://127.0.0.1:8000/api/v1';

function AttributionClasses() {
  const [enseignants, setEnseignants] = useState([]);
  const [classes, setClasses] = useState([]);
  const [filieres, setFilieres] = useState([]);
  const [attributions, setAttributions] = useState([]);
  const [selectedEnseignant, setSelectedEnseignant] = useState('');
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [anneeScolaire] = useState('2025-2026');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [viewMode, setViewMode] = useState('attribution'); // 'attribution' ou 'liste'
  const [searchTerm, setSearchTerm] = useState('');
  const [filterMatiere, setFilterMatiere] = useState('all');
  const [filterFiliere, setFilterFiliere] = useState('all');
  const [filterNiveau, setFilterNiveau] = useState('all');

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const [enseignantsRes, classesRes, attributionsRes, filieresRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/admin/enseignants`),
        axios.get(`${API_BASE_URL}/admin/classes`),
        axios.get(`${API_BASE_URL}/admin/enseignants-classes?annee_scolaire=${anneeScolaire}`),
        axios.get(`${API_BASE_URL}/filieres`)
      ]);

      console.log('Attribution data:', {
        enseignants: enseignantsRes.data,
        classes: classesRes.data,
        attributions: attributionsRes.data,
        filieres: filieresRes.data
      });

      // Filtrer uniquement les enseignants actifs
      const enseignantsData = Array.isArray(enseignantsRes.data.enseignants) 
        ? enseignantsRes.data.enseignants 
        : Array.isArray(enseignantsRes.data.data)
        ? enseignantsRes.data.data
        : [];
      
      const enseignantsActifs = enseignantsData.filter(ens => ens.actif === true || ens.actif === 1);
      
      setEnseignants(enseignantsActifs);
      setClasses(Array.isArray(classesRes.data.classes) ? classesRes.data.classes : []);
      setAttributions(Array.isArray(attributionsRes.data.attributions) ? attributionsRes.data.attributions : []);
      setFilieres(Array.isArray(filieresRes.data) ? filieresRes.data : []);
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
      showMessage('error', 'Erreur lors du chargement des données');
      setEnseignants([]);
      setClasses([]);
      setAttributions([]);
      setFilieres([]);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const handleClasseToggle = (classeId) => {
    setSelectedClasses(prev => {
      if (prev.includes(classeId)) {
        return prev.filter(id => id !== classeId);
      } else {
        return [...prev, classeId];
      }
    });
  };

  const handleAssigner = async () => {
    if (!selectedEnseignant) {
      showMessage('error', 'Veuillez sélectionner un enseignant');
      return;
    }

    if (selectedClasses.length === 0) {
      showMessage('error', 'Veuillez sélectionner au moins une classe');
      return;
    }

    try {
      const response = await axios.post(`${API_BASE_URL}/admin/enseignants-classes/assigner-multiples`, {
        enseignant_id: selectedEnseignant,
        classe_ids: selectedClasses,
        annee_scolaire: anneeScolaire
      });

      showMessage('success', response.data.message);
      setSelectedEnseignant('');
      setSelectedClasses([]);
      fetchData();
    } catch (error) {
      console.error('Erreur lors de l\'attribution:', error);
      showMessage('error', error.response?.data?.message || 'Erreur lors de l\'attribution');
    }
  };

  const handleRetirer = async (enseignantId, classeId) => {
    if (!window.confirm('Voulez-vous vraiment retirer cet enseignant de cette classe?')) {
      return;
    }

    try {
      const response = await axios.delete(`${API_BASE_URL}/admin/enseignants-classes/retirer`, {
        data: {
          enseignant_id: enseignantId,
          classe_id: classeId,
          annee_scolaire: anneeScolaire
        }
      });

      showMessage('success', response.data.message);
      fetchData();
    } catch (error) {
      console.error('Erreur lors du retrait:', error);
      showMessage('error', error.response?.data?.message || 'Erreur lors du retrait');
    }
  };

  const getClassesParEnseignant = (enseignantId) => {
    return attributions.filter(attr => attr.enseignant_id === enseignantId);
  };

  const getEnseignantsParClasse = (classeId) => {
    return attributions.filter(attr => attr.classe_id === classeId);
  };

  const getUniqueMatieres = () => {
    const matieres = [...new Set(enseignants.map(e => e.matiere))];
    return matieres.filter(Boolean).sort();
  };

  const getUniqueNiveaux = () => {
    const niveaux = [...new Set(classes.map(c => c.niveau))];
    return niveaux.filter(Boolean).sort();
  };

  const getFilteredEnseignants = () => {
    let filtered = enseignants;
    
    if (searchTerm) {
      filtered = filtered.filter(ens => 
        `${ens.prenom} ${ens.nom}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ens.matiere?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ens.filiere?.titre?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (filterMatiere !== 'all') {
      filtered = filtered.filter(ens => ens.matiere === filterMatiere);
    }
    
    if (filterFiliere !== 'all') {
      filtered = filtered.filter(ens => ens.filiere_id === parseInt(filterFiliere));
    }
    
    return filtered;
  };

  const getFilteredClasses = () => {
    let filtered = classes;
    
    if (filterNiveau !== 'all') {
      filtered = filtered.filter(classe => classe.niveau === filterNiveau);
    }
    
    return filtered;
  };

  const getSelectedEnseignantInfo = () => {
    return enseignants.find(e => e.id === parseInt(selectedEnseignant));
  };

  return (
    <div className="admin-layout">
      <DashboardHeader />
      <AdminNavigationBar 
        title="Attribution des Classes" 
        onBack={() => window.navigateTo && window.navigateTo('admin-dashboard')}
      />
      <div className="admin-container">
        <main className="admin-content admin-content-full">
          <div className="attribution-classes-container">
            
            {message.text && (
              <div className={`alert-message ${message.type}`}>
                <span className="alert-icon">{message.type === 'success' ? '✅' : '❌'}</span>
                <span>{message.text}</span>
              </div>
            )}

            <div className="view-toggle-modern">
              <button
                className={`toggle-btn ${viewMode === 'attribution' ? 'active' : ''}`}
                onClick={() => setViewMode('attribution')}
              >
                <span className="btn-icon">➕</span>
                Assigner des Classes
              </button>
              <button
                className={`toggle-btn ${viewMode === 'liste' ? 'active' : ''}`}
                onClick={() => setViewMode('liste')}
              >
                <span className="btn-icon">📋</span>
                Voir les Attributions
              </button>
            </div>

            {viewMode === 'attribution' ? (
              <div className="attribution-section-modern">
                
                {/* Étape 1: Sélection de l'enseignant */}
                <div className="step-card">
                  <div className="step-header">
                    <span className="step-number">1</span>
                    <h2>Sélectionner un enseignant</h2>
                  </div>
                  
                  <div className="search-filter-bar">
                    <div className="search-box">
                      <span className="search-icon">🔍</span>
                      <input
                        type="text"
                        placeholder="Rechercher un enseignant..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="search-input"
                      />
                    </div>
                    
                    <select
                      value={filterFiliere}
                      onChange={(e) => setFilterFiliere(e.target.value)}
                      className="filter-select"
                    >
                      <option value="all">Toutes les filières</option>
                      {filieres.map(filiere => (
                        <option key={filiere.id} value={filiere.id}>{filiere.titre}</option>
                      ))}
                    </select>
                    
                    <select
                      value={filterMatiere}
                      onChange={(e) => setFilterMatiere(e.target.value)}
                      className="filter-select"
                    >
                      <option value="all">Toutes les matières</option>
                      {getUniqueMatieres().map(matiere => (
                        <option key={matiere} value={matiere}>{matiere}</option>
                      ))}
                    </select>
                  </div>

                  <div className="enseignants-grid">
                    {getFilteredEnseignants().map(ens => {
                      const classesCount = getClassesParEnseignant(ens.id).length;
                      const isSelected = selectedEnseignant === ens.id.toString();
                      
                      return (
                        <div
                          key={ens.id}
                          className={`enseignant-card-modern ${isSelected ? 'selected' : ''}`}
                          onClick={() => {
                            setSelectedEnseignant(ens.id.toString());
                            setSelectedClasses([]);
                          }}
                        >
                          <div className="enseignant-avatar">
                            {ens.prenom?.charAt(0)}{ens.nom?.charAt(0)}
                          </div>
                          <div className="enseignant-info">
                            <h3>{ens.prenom} {ens.nom}</h3>
                            <p className="enseignant-matiere">{ens.matiere}</p>
                            {ens.filiere && (
                              <p className="enseignant-filiere">📚 {ens.filiere.titre}</p>
                            )}
                            <div className="enseignant-stats">
                              <span className="stat-badge">
                                📚 {classesCount} classe{classesCount > 1 ? 's' : ''}
                              </span>
                            </div>
                          </div>
                          {isSelected && <div className="selected-indicator">✓</div>}
                        </div>
                      );
                    })}
                  </div>

                  {getFilteredEnseignants().length === 0 && (
                    <div className="empty-state">
                      <span className="empty-icon">🔍</span>
                      <p>Aucun enseignant trouvé</p>
                    </div>
                  )}
                </div>

                {/* Étape 2: Sélection des classes */}
                {selectedEnseignant && (
                  <div className="step-card">
                    <div className="step-header">
                      <span className="step-number">2</span>
                      <h2>Sélectionner les classes</h2>
                    </div>

                    <div className="selected-teacher-info">
                      <div className="teacher-badge">
                        <span className="badge-icon">👨‍🏫</span>
                        <div>
                          <strong>{getSelectedEnseignantInfo()?.prenom} {getSelectedEnseignantInfo()?.nom}</strong>
                          <span>{getSelectedEnseignantInfo()?.matiere}</span>
                          {getSelectedEnseignantInfo()?.filiere && (
                            <span className="teacher-filiere">📚 {getSelectedEnseignantInfo()?.filiere.titre}</span>
                          )}
                        </div>
                      </div>
                      {selectedClasses.length > 0 && (
                        <div className="selection-count">
                          {selectedClasses.length} classe{selectedClasses.length > 1 ? 's' : ''} sélectionnée{selectedClasses.length > 1 ? 's' : ''}
                        </div>
                      )}
                    </div>

                    <div className="classes-filter-bar">
                      <label>Filtrer par niveau :</label>
                      <select
                        value={filterNiveau}
                        onChange={(e) => setFilterNiveau(e.target.value)}
                        className="filter-select-small"
                      >
                        <option value="all">Tous les niveaux</option>
                        {getUniqueNiveaux().map(niveau => (
                          <option key={niveau} value={niveau}>{niveau}</option>
                        ))}
                      </select>
                    </div>

                    <div className="classes-grid-modern">
                      {getFilteredClasses().map(classe => {
                        const dejaAssigne = attributions.some(
                          attr => attr.enseignant_id === parseInt(selectedEnseignant) && attr.classe_id === classe.id
                        );
                        const isSelected = selectedClasses.includes(classe.id);
                        const enseignantsCount = getEnseignantsParClasse(classe.id).length;
                        
                        return (
                          <div
                            key={classe.id}
                            className={`classe-card-modern ${isSelected ? 'selected' : ''} ${dejaAssigne ? 'disabled' : ''}`}
                            onClick={() => !dejaAssigne && handleClasseToggle(classe.id)}
                          >
                            <div className="classe-header">
                              <h3>{classe.nom}</h3>
                              {isSelected && <span className="check-icon">✓</span>}
                            </div>
                            <div className="classe-details">
                              <div className="detail-item">
                                <span className="detail-icon">📊</span>
                                <span>{classe.niveau}</span>
                              </div>
                              <div className="detail-item">
                                <span className="detail-icon">👥</span>
                                <span>{classe.effectif} élèves</span>
                              </div>
                              <div className="detail-item">
                                <span className="detail-icon">👨‍🏫</span>
                                <span>{enseignantsCount} prof{enseignantsCount > 1 ? 's' : ''}</span>
                              </div>
                            </div>
                            {dejaAssigne && (
                              <div className="already-assigned-badge">
                                Déjà assigné
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {selectedClasses.length > 0 && (
                      <div className="action-bar">
                        <button className="btn-cancel" onClick={() => setSelectedClasses([])}>
                          Annuler la sélection
                        </button>
                        <button className="btn-assign" onClick={handleAssigner}>
                          <span className="btn-icon">✓</span>
                          Assigner {selectedClasses.length} classe{selectedClasses.length > 1 ? 's' : ''}
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {!selectedEnseignant && (
                  <div className="instruction-card">
                    <span className="instruction-icon">💡</span>
                    <div>
                      <h3>Comment assigner des classes ?</h3>
                      <ol>
                        <li>Sélectionnez un enseignant dans la liste ci-dessus</li>
                        <li>Choisissez une ou plusieurs classes à lui assigner</li>
                        <li>Confirmez l'attribution</li>
                      </ol>
                      <p className="note">Les classes assignées apparaîtront automatiquement dans le dashboard du professeur.</p>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="liste-section-modern">
                <div className="stats-cards-modern">
                  <div className="stat-card-modern">
                    <div className="stat-icon">👨‍🏫</div>
                    <div className="stat-content">
                      <div className="stat-value">{enseignants.length}</div>
                      <div className="stat-label">Enseignants</div>
                    </div>
                  </div>
                  <div className="stat-card-modern">
                    <div className="stat-icon">🏫</div>
                    <div className="stat-content">
                      <div className="stat-value">{classes.length}</div>
                      <div className="stat-label">Classes</div>
                    </div>
                  </div>
                  <div className="stat-card-modern">
                    <div className="stat-icon">🔗</div>
                    <div className="stat-content">
                      <div className="stat-value">{attributions.length}</div>
                      <div className="stat-label">Attributions</div>
                    </div>
                  </div>
                </div>

                <div className="attributions-list-modern">
                  <h2>Attributions par Enseignant</h2>
                  {getFilteredEnseignants().map(enseignant => {
                    const classesEns = getClassesParEnseignant(enseignant.id);
                    
                    return (
                      <div key={enseignant.id} className="enseignant-attribution-card">
                        <div className="attribution-header">
                          <div className="teacher-info">
                            <div className="teacher-avatar-small">
                              {enseignant.prenom?.charAt(0)}{enseignant.nom?.charAt(0)}
                            </div>
                            <div>
                              <h3>{enseignant.prenom} {enseignant.nom}</h3>
                              <p className="teacher-matiere">{enseignant.matiere}</p>
                              {enseignant.filiere && (
                                <p className="teacher-filiere-small">📚 {enseignant.filiere.titre}</p>
                              )}
                            </div>
                          </div>
                          <div className="classes-count-badge">
                            {classesEns.length} classe{classesEns.length > 1 ? 's' : ''}
                          </div>
                        </div>
                        
                        {classesEns.length > 0 ? (
                          <div className="classes-list-modern">
                            {classesEns.map(attr => (
                              <div key={attr.id} className="classe-item-modern">
                                <div className="classe-item-info">
                                  <span className="classe-name">{attr.classe_nom}</span>
                                  <span className="classe-level">{attr.niveau}</span>
                                </div>
                                <button
                                  className="btn-remove"
                                  onClick={() => handleRetirer(attr.enseignant_id, attr.classe_id)}
                                  title="Retirer cette classe"
                                >
                                  <span>🗑️</span>
                                </button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="no-classes">
                            <span className="no-classes-icon">📭</span>
                            <p>Aucune classe assignée</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

export default AttributionClasses;
